import { finalizeEvent, type EventTemplate, type Event as NostrEvent } from "nostr-tools/pure";
import { RELAY_URL } from "./api";
import { secretKeyBytes, type Identity } from "./identity";

export type ConnState = "connecting" | "authing" | "ready" | "closed" | "error";

export interface ChannelMeta {
  id: string;
  name: string;
  about: string;
  private: boolean;
  hidden: boolean;
  eventId: string;
  createdAt: number;
}

export interface ChannelMember {
  pubkey: string;
  role?: string;
}

export interface ChatMessage {
  id: string;
  channelId: string;
  pubkey: string;
  content: string;
  createdAt: number;
}

export interface HuddleSession {
  parentChannelId: string;
  ephemeralChannelId: string;
  startedBy: string;
  createdAt: number;
  eventId: string;
}

type Listener = () => void;

function tagValue(event: NostrEvent, name: string): string | undefined {
  const tag = event.tags.find((t) => t[0] === name);
  return tag?.[1];
}

function parseChannel(event: NostrEvent): ChannelMeta | null {
  const id = tagValue(event, "d");
  if (!id) return null;
  return {
    id,
    name: tagValue(event, "name") || id.slice(0, 8),
    about: tagValue(event, "about") || event.content || "",
    private: event.tags.some((t) => t[0] === "private"),
    hidden: event.tags.some((t) => t[0] === "hidden"),
    eventId: event.id,
    createdAt: event.created_at,
  };
}

export class BuzzRelayClient {
  readonly relayUrl = RELAY_URL;
  state: ConnState = "connecting";
  statusText = "Connecting…";
  channels = new Map<string, ChannelMeta>();
  members = new Map<string, ChannelMember[]>();
  messages = new Map<string, ChatMessage[]>();
  profiles = new Map<string, { name?: string; about?: string }>();
  huddles = new Map<string, HuddleSession>();

  private socket: WebSocket | null = null;
  private identity: Identity;
  private listeners = new Set<Listener>();
  private subSeq = 0;
  private messageSubId: string | null = null;
  private memberSubId: string | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private disposed = false;

  private pendingAuthId: string | null = null;
  private bootstrapped = false;

  constructor(identity: Identity) {
    this.identity = identity;
  }

  get pubkey(): string {
    return this.identity.pubkey;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit() {
    for (const listener of this.listeners) listener();
  }

  private setState(state: ConnState, statusText: string) {
    this.state = state;
    this.statusText = statusText;
    this.emit();
  }

  connect() {
    if (this.disposed) return;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.socket?.close();
    this.setState("connecting", `Connecting to ${this.relayUrl}`);
    const socket = new WebSocket(this.relayUrl);
    this.socket = socket;

    socket.addEventListener("open", () => {
      this.setState("authing", "Connected — waiting for NIP-42 challenge");
    });

    socket.addEventListener("message", (event) => {
      void this.onFrame(typeof event.data === "string" ? event.data : "");
    });

    socket.addEventListener("error", () => {
      this.setState("error", "WebSocket error");
    });

    socket.addEventListener("close", () => {
      this.setState("closed", "Disconnected");
      if (!this.disposed) {
        this.reconnectTimer = setTimeout(() => this.connect(), 2500);
      }
    });
  }

  dispose() {
    this.disposed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = null;
  }

  private send(payload: unknown) {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      throw new Error("Relay is not connected");
    }
    this.socket.send(JSON.stringify(payload));
  }

  private nextSub(prefix: string): string {
    this.subSeq += 1;
    return `${prefix}-${this.subSeq}`;
  }

  private sign(template: EventTemplate): NostrEvent {
    return finalizeEvent(template, secretKeyBytes(this.identity));
  }

  private async onFrame(raw: string) {
    if (!raw) return;
    let frame: unknown;
    try {
      frame = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(frame) || frame.length < 2) return;
    const type = frame[0];

    if (type === "AUTH" && typeof frame[1] === "string") {
      await this.handleAuthChallenge(frame[1]);
      return;
    }

    if (type === "EVENT" && frame.length >= 3 && typeof frame[2] === "object") {
      this.handleEvent(frame[2] as NostrEvent);
      return;
    }

    if (type === "OK" && frame.length >= 3) {
      const eventId = typeof frame[1] === "string" ? frame[1] : "";
      const ok = Boolean(frame[2]);
      const message = typeof frame[3] === "string" ? frame[3] : "";
      if (this.pendingAuthId && eventId === this.pendingAuthId) {
        this.pendingAuthId = null;
        if (!ok) {
          this.setState("error", message || "NIP-42 AUTH rejected");
          return;
        }
        this.setState("ready", "Authenticated");
        if (!this.bootstrapped) {
          this.bootstrapped = true;
          this.bootstrap();
        }
        return;
      }
      if (!ok) {
        this.statusText = message || "EVENT rejected";
        this.emit();
      }
      return;
    }

    if (type === "NOTICE" && typeof frame[1] === "string") {
      this.statusText = frame[1];
      this.emit();
    }
  }

  private async handleAuthChallenge(challenge: string) {
    this.setState("authing", "Authenticating (NIP-42)…");
    const authEvent = this.sign({
      kind: 22242,
      created_at: Math.floor(Date.now() / 1000),
      tags: [
        ["relay", this.relayUrl],
        ["challenge", challenge],
      ],
      content: "",
    });
    this.pendingAuthId = authEvent.id;
    this.send(["AUTH", authEvent]);
    // Fallback if the relay does not echo AUTH OK (some builds only NOTICE).
    setTimeout(() => {
      if (this.disposed || this.state === "ready" || this.state === "error") return;
      this.setState("ready", "Authenticated (assumed)");
      if (!this.bootstrapped) {
        this.bootstrapped = true;
        this.bootstrap();
      }
    }, 800);
  }

  private bootstrap() {
    const groupsSub = this.nextSub("groups");
    this.send(["REQ", groupsSub, { kinds: [39000], limit: 200 }]);

    const membershipSub = this.nextSub("membership");
    this.send([
      "REQ",
      membershipSub,
      { kinds: [44100, 44101], "#p": [this.identity.pubkey], limit: 100 },
    ]);

    const profileSub = this.nextSub("profile");
    this.send(["REQ", profileSub, { kinds: [0], authors: [this.identity.pubkey], limit: 1 }]);
  }

  private handleEvent(event: NostrEvent) {
    switch (event.kind) {
      case 39000: {
        const channel = parseChannel(event);
        if (!channel || channel.hidden) break;
        const existing = this.channels.get(channel.id);
        if (!existing || channel.createdAt >= existing.createdAt) {
          this.channels.set(channel.id, channel);
          this.emit();
        }
        break;
      }
      case 39001:
      case 39002: {
        const id = tagValue(event, "d");
        if (!id) break;
        const members: ChannelMember[] = event.tags
          .filter((t) => t[0] === "p" && t[1])
          .map((t) => ({ pubkey: t[1]!, role: t[2] }));
        this.members.set(id, members);
        this.emit();
        break;
      }
      case 9: {
        const channelId = tagValue(event, "h");
        if (!channelId) break;
        const list = this.messages.get(channelId) ?? [];
        if (list.some((m) => m.id === event.id)) break;
        list.push({
          id: event.id,
          channelId,
          pubkey: event.pubkey,
          content: event.content,
          createdAt: event.created_at,
        });
        list.sort((a, b) => a.createdAt - b.createdAt);
        this.messages.set(channelId, list.slice(-500));
        this.emit();
        break;
      }
      case 0: {
        try {
          const meta = JSON.parse(event.content) as { name?: string; about?: string };
          this.profiles.set(event.pubkey, meta);
          this.emit();
        } catch {
          // ignore bad profile JSON
        }
        break;
      }
      case 44100: {
        const channelId = tagValue(event, "h");
        if (channelId && !this.channels.has(channelId)) {
          this.send(["REQ", this.nextSub("groups-refresh"), { kinds: [39000], limit: 200 }]);
        }
        break;
      }
      case 48100: {
        const parent = tagValue(event, "h");
        if (!parent) break;
        let ephemeral = "";
        try {
          ephemeral = (JSON.parse(event.content) as { ephemeral_channel_id?: string })
            .ephemeral_channel_id || "";
        } catch {
          ephemeral = "";
        }
        if (!ephemeral) break;
        this.huddles.set(parent, {
          parentChannelId: parent,
          ephemeralChannelId: ephemeral,
          startedBy: event.pubkey,
          createdAt: event.created_at,
          eventId: event.id,
        });
        this.emit();
        break;
      }
      case 48103: {
        const parent = tagValue(event, "h");
        if (parent) {
          this.huddles.delete(parent);
          this.emit();
        }
        break;
      }
      default:
        break;
    }
  }

  selectChannel(channelId: string | null) {
    if (this.messageSubId) {
      this.send(["CLOSE", this.messageSubId]);
      this.messageSubId = null;
    }
    if (this.memberSubId) {
      this.send(["CLOSE", this.memberSubId]);
      this.memberSubId = null;
    }
    if (!channelId || this.state !== "ready") {
      this.emit();
      return;
    }

    this.messageSubId = this.nextSub("msgs");
    this.send([
      "REQ",
      this.messageSubId,
      { kinds: [9, 48100, 48101, 48102, 48103], "#h": [channelId], limit: 120 },
    ]);

    this.memberSubId = this.nextSub("members");
    this.send([
      "REQ",
      this.memberSubId,
      { kinds: [39001, 39002], "#d": [channelId], limit: 10 },
    ]);
    this.emit();
  }

  async publishProfile(displayName: string) {
    const event = this.sign({
      kind: 0,
      created_at: Math.floor(Date.now() / 1000),
      tags: [],
      content: JSON.stringify({
        name: displayName.trim() || "CDMSuite operator",
        about: "Buzz web workspace · Chief Trades",
      }),
    });
    this.send(["EVENT", event]);
  }

  async createChannel(name: string, about = "", visibility: "open" | "private" = "open") {
    const cleaned = name.trim().replace(/^#/, "");
    if (!cleaned) throw new Error("Channel name required");
    const event = this.sign({
      kind: 9007,
      created_at: Math.floor(Date.now() / 1000),
      tags: [
        ["name", cleaned],
        ["visibility", visibility],
        ["channel_type", "stream"],
        ...(about.trim() ? [["about", about.trim()]] : []),
      ],
      content: about.trim(),
    });
    this.send(["EVENT", event]);
    // Discovery events are relay-emitted; re-query shortly after.
    setTimeout(() => {
      if (this.disposed) return;
      this.send(["REQ", this.nextSub("groups-after-create"), { kinds: [39000], limit: 200 }]);
    }, 600);
  }

  async joinChannel(channelId: string) {
    const event = this.sign({
      kind: 9021,
      created_at: Math.floor(Date.now() / 1000),
      tags: [["h", channelId]],
      content: "",
    });
    this.send(["EVENT", event]);
    setTimeout(() => this.selectChannel(channelId), 400);
  }

  async sendMessage(channelId: string, content: string, mentionPubkeys: string[] = []) {
    const text = content.trim();
    if (!text) return;
    const event = this.sign({
      kind: 9,
      created_at: Math.floor(Date.now() / 1000),
      tags: [["h", channelId], ...mentionPubkeys.map((p) => ["p", p])],
      content: text,
    });
    this.send(["EVENT", event]);
    const list = this.messages.get(channelId) ?? [];
    if (!list.some((m) => m.id === event.id)) {
      list.push({
        id: event.id,
        channelId,
        pubkey: event.pubkey,
        content: text,
        createdAt: event.created_at,
      });
      this.messages.set(channelId, list);
      this.emit();
    }
  }

  /** Announce a Cloudflare mesh huddle on the Buzz timeline (kind 48100). */
  async startHuddleAnnouncement(parentChannelId: string): Promise<string> {
    const ephemeralChannelId = crypto.randomUUID();
    const event = this.sign({
      kind: 48100,
      created_at: Math.floor(Date.now() / 1000),
      tags: [["h", parentChannelId]],
      content: JSON.stringify({
        ephemeral_channel_id: ephemeralChannelId,
        media: "cloudflare-mesh",
        signaling: `/platform/huddle/ws/${parentChannelId}`,
      }),
    });
    this.send(["EVENT", event]);
    this.huddles.set(parentChannelId, {
      parentChannelId,
      ephemeralChannelId,
      startedBy: this.identity.pubkey,
      createdAt: event.created_at,
      eventId: event.id,
    });
    this.emit();
    return ephemeralChannelId;
  }

  async endHuddleAnnouncement(parentChannelId: string) {
    const session = this.huddles.get(parentChannelId);
    const event = this.sign({
      kind: 48103,
      created_at: Math.floor(Date.now() / 1000),
      tags: [["h", parentChannelId]],
      content: JSON.stringify({
        ephemeral_channel_id: session?.ephemeralChannelId ?? null,
      }),
    });
    this.send(["EVENT", event]);
    this.huddles.delete(parentChannelId);
    this.emit();
  }

  huddleFor(channelId: string): HuddleSession | null {
    return this.huddles.get(channelId) ?? null;
  }

  channelList(): ChannelMeta[] {
    return [...this.channels.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  messagesFor(channelId: string): ChatMessage[] {
    return this.messages.get(channelId) ?? [];
  }

  membersFor(channelId: string): ChannelMember[] {
    return this.members.get(channelId) ?? [];
  }

  displayName(pubkey: string): string {
    return this.profiles.get(pubkey)?.name || `${pubkey.slice(0, 8)}…`;
  }
}
